import Image from 'next/image';
import React, { useCallback, useId } from 'react';
import { useAnalytics } from '@provider-directory/components/analytics/analytics-provider';
import { useOrgContext } from '@provider-directory/components/org-context/org-context-provider';
import { MEACFooterAccordion } from './footer-accordion';
import {
  buildMEACFooterLinks,
  complianceLinks,
  copyrightText,
  findCareTitleHref,
  forProfessionalsTitleHref,
  locationsTitleHref,
  patientResourcesTitleHref,
} from './footer-data';

import './meac-footer.css';

const MEAC_LOGO_URL =
  'https://assets.montefioreeinstein.org/meac/logo/meac_logo_rgb.svg';

export const MEACFooter: React.FC = () => {
  const footerHeadingId = useId();
  const analytics = useAnalytics();
  const { config } = useOrgContext();
  const {
    findCareLinks,
    locationsLinks,
    patientResourcesLinks,
    forProfessionalsLinks,
    accordionData,
  } = buildMEACFooterLinks(config.companyUrl);

  const trackLink = useCallback(
    (clickText: string) => {
      analytics.navigationClickEvent({
        navigationType: 'footer',
        clickText,
        linkType: 'link',
      });
    },
    [analytics],
  );

  return (
    <footer className={'meac-footer'} aria-labelledby={footerHeadingId}>
      <h2 id={footerHeadingId} className={'sr-only'}>
        {'Montefiore Einstein Advanced Care'}
      </h2>
      <div className={'meac-wrap'}>
        <div className={'meac-logo'}>
          <Image
            src={MEAC_LOGO_URL}
            alt={'Montefiore Einstein Advanced Care'}
            width={243}
            height={48}
            className={'meac-footer__logo-img'}
            priority={false}
          />
        </div>
        <hr className={'meac-footer__divider'} />
        <div
          className={'meac-footer__main-links meac-footer__main-links--desktop'}
        >
          <div>
            <h3 className={'meac-footer__column-title'}>
              <a
                href={findCareTitleHref}
                className={'meac-footer__column-title-link'}
                onClick={() => {
                  trackLink('Find Care');
                }}
              >
                Find Care
              </a>
            </h3>
            <ul className={'meac-footer__links-list'}>
              {findCareLinks.map((link) => (
                <li key={link.text}>
                  <a
                    href={link.href}
                    className={'meac-footer__link'}
                    onClick={() => {
                      trackLink(link.analyticsText);
                    }}
                  >
                    {link.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className={'meac-footer__column-title'}>
              <a
                href={locationsTitleHref}
                className={'meac-footer__column-title-link'}
                onClick={() => {
                  trackLink('Locations');
                }}
              >
                Locations
              </a>
            </h3>
            <ul className={'meac-footer__links-list'}>
              {locationsLinks.map((link) => (
                <li key={link.text}>
                  <a
                    href={link.href}
                    className={'meac-footer__link'}
                    onClick={() => {
                      trackLink(link.analyticsText);
                    }}
                  >
                    {link.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className={'meac-footer__column-title'}>
              <a
                href={patientResourcesTitleHref}
                className={'meac-footer__column-title-link'}
                onClick={() => {
                  trackLink('Patient Resources');
                }}
              >
                Patient Resources
              </a>
            </h3>
            <ul className={'meac-footer__links-list'}>
              {patientResourcesLinks.map((link) => (
                <li key={link.text}>
                  <a
                    href={link.href}
                    className={'meac-footer__link'}
                    onClick={() => {
                      trackLink(link.analyticsText);
                    }}
                  >
                    {link.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className={'meac-footer__column-title'}>
              <a
                href={forProfessionalsTitleHref}
                className={'meac-footer__column-title-link'}
                onClick={() => {
                  trackLink('For Professionals');
                }}
              >
                For Professionals
              </a>
            </h3>
            <ul className={'meac-footer__links-list'}>
              {forProfessionalsLinks.map((link) => (
                <li key={link.text}>
                  <a
                    href={link.href}
                    className={'meac-footer__link'}
                    onClick={() => {
                      trackLink(link.analyticsText);
                    }}
                  >
                    {link.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className={'meac-footer__accordion-mobile'}>
          <hr className={'meac-footer__divider meac-footer__divider--mobile'} />
          <MEACFooterAccordion accordionData={accordionData} />
        </div>
        <div className={'meac-footer__bottom'}>
          <div className={'meac-footer__compliance-links'}>
            {complianceLinks.map((link) => {
              if (link.onClick) {
                return (
                  <button
                    key={link.text}
                    onClick={() => {
                      link.onClick?.();
                      trackLink(link.analyticsText);
                    }}
                    className={'meac-footer__link'}
                    type={'button'}
                  >
                    {link.text}
                  </button>
                );
              }
              return (
                <a
                  key={link.text}
                  href={link.href}
                  className={'meac-footer__link'}
                  onClick={() => {
                    trackLink(link.analyticsText);
                  }}
                >
                  {link.text}
                </a>
              );
            })}
          </div>
          <hr className={'meac-footer__copyright-divider'} />
          <div className={'meac-footer__copyright'}>{copyrightText}</div>
        </div>
      </div>
    </footer>
  );
};
